<template>
    <div class="space-y-3">
        <!-- Enable Recurring Toggle -->
        <div v-if="!hideHeader" class="flex items-start justify-between gap-3">
            <div class="min-w-0">
                <label class="text-sm font-medium">Make This Task Recurring</label>
                <p class="mt-1 text-xs text-muted-foreground">
                    {{ getRecurringSummary() }}
                </p>
            </div>
            <Switch :model-value="isEnabled" @update:model-value="handleEnabledChange" />
        </div>

        <!-- Recurring Options (show when enabled) -->
        <div
            v-if="isEnabled"
            :class="[
                'space-y-3',
                hideHeader ? '' : 'border-t border-border/70 pt-3',
            ]"
        >
            <!-- Frequency Selection -->
            <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div>
                    <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Frequency</label>
                    <SearchableSelectMenu
                        :model-value="config.frequency"
                        :options="frequencyOptions"
                        show-all-by-default
                        variant="form"
                        @update:model-value="handleFrequencyChange($event as RecurringConfig['frequency'])"
                    />
                </div>

                <div>
                    <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Ends</label>
                    <DatePicker
                        :model-value="config.endDate"
                        placeholder="Never"
                        @update:model-value="handleEndDateChange"
                    />
                </div>
            </div>

            <!-- Weekly Options -->
            <div v-if="config.frequency === 'weekly'" class="space-y-2">
                <label class="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Repeat On</label>
                <div class="grid grid-cols-4 gap-2 sm:grid-cols-7">
                    <button
                        v-for="(day, index) in weekDays"
                        :key="day"
                        type="button"
                        class="rounded-xl border px-2 py-2 text-center text-xs font-medium transition-colors"
                        :class="isWeekdaySelected(index)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border/70 bg-background text-muted-foreground hover:bg-muted/40'"
                        @click="toggleWeekday(index)"
                    >
                        {{ day }}
                    </button>
                </div>
            </div>

            <!-- Monthly Options -->
            <div v-if="config.frequency === 'monthly'" class="space-y-2">
                <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Day Of Month</label>
                <SearchableSelectMenu
                    :model-value="String(config.dayOfMonth ?? '')"
                    :options="dayOfMonthOptions"
                    placeholder="Select day (1-31)"
                    allow-clear
                    clear-label="Select day (1-31)"
                    show-all-by-default
                    :visible-limit="31"
                    variant="form"
                    @update:model-value="handleDayOfMonthChange"
                />
            </div>

            <div v-if="showSummaryFooter" class="rounded-xl border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                {{ getRecurringSummary() }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * RecurringTaskConfig Component
 * 
 * Provides UI for configuring recurring task patterns.
 * Supports daily, weekly, monthly, and yearly recurrence.
 * 
 * Features:
 * - Enable/disable toggle
 * - Frequency selection
 * - Weekly day selection
 * - Monthly day selection
 * - Optional end date
 * - Human-readable summary
 * 
 * Two-way binding:
 * - v-model for task's recurring config object
 * 
 * @example Usage
 * <RecurringTaskConfig v-model="task.recurring" />
 */
import { ref, computed, watch } from 'vue';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';

const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
] as const;
const dayOfMonthOptions = Array.from({ length: 31 }, (_, index) => {
    const day = index + 1;
    return { value: String(day), label: `${day}${getDayOrdinal(day)}` };
});

interface RecurringConfig {
    enabled: boolean;
    frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    endDate?: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
}

interface Props {
    modelValue?: RecurringConfig;
    hideHeader?: boolean;
    showSummaryFooter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: () => ({
        enabled: false,
        frequency: 'daily',
        daysOfWeek: [1, 2, 3, 4, 5] // Mon-Fri by default
    }),
    hideHeader: false,
    showSummaryFooter: true,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: RecurringConfig): void;
}>();

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ========================================================================
// STATE
// ========================================================================

const config = ref<RecurringConfig>(JSON.parse(JSON.stringify(props.modelValue || {
    enabled: false,
    frequency: 'daily',
    daysOfWeek: [1, 2, 3, 4, 5]
})));

// ========================================================================
// COMPUTED
// ========================================================================

const isEnabled = computed({
    get: () => config.value.enabled || false,
    set: (value: boolean) => updateConfig({ enabled: value }),
});

// ========================================================================
// METHODS
// ========================================================================

/**
 * Toggle recurring enabled state
 */
function handleEnabledChange(value: boolean) {
    updateConfig({ enabled: value });
}

/**
 * Handle frequency change
 */
function handleFrequencyChange(value?: RecurringConfig['frequency']) {
    const nextConfig: RecurringConfig = {
        ...config.value,
        frequency: value || 'daily',
    };

    if (nextConfig.frequency === 'weekly' && !nextConfig.daysOfWeek?.length) {
        nextConfig.daysOfWeek = [1, 2, 3, 4, 5];
    }

    if (nextConfig.frequency !== 'monthly') {
        nextConfig.dayOfMonth = undefined;
    }

    updateConfig(nextConfig);
}

/**
 * Check if a weekday is selected
 */
function isWeekdaySelected(dayIndex: number): boolean {
    return config.value.daysOfWeek?.includes(dayIndex) ?? false;
}

/**
 * Toggle a weekday
 */
function toggleWeekday(dayIndex: number) {
    const nextDays = [...(config.value.daysOfWeek || [])];

    const index = nextDays.indexOf(dayIndex);
    if (index > -1) {
        nextDays.splice(index, 1);
    } else {
        nextDays.push(dayIndex);
    }
    nextDays.sort();

    updateConfig({ daysOfWeek: nextDays });
}

function handleDayOfMonthChange(value: string) {
    updateConfig({
        dayOfMonth: value ? Number(value) : undefined,
    });
}

function handleEndDateChange(value: string) {
    updateConfig({
        endDate: value || undefined,
    });
}

/**
 * Get ordinal suffix for day number (1st, 2nd, 3rd, etc.)
 */
function getDayOrdinal(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
}

/**
 * Get human-readable summary of recurring pattern
 */
function getRecurringSummary(): string {
    if (!isEnabled.value) {
        return 'This task does not repeat';
    }

    let summary = 'Repeats ';

    switch (config.value.frequency) {
        case 'daily':
            summary += 'every day';
            break;
        case 'weekly':
            const selectedDays = config.value.daysOfWeek
                ?.map(i => weekDays[i])
                .join(', ') || 'no days selected';
            summary += `on ${selectedDays}`;
            break;
        case 'monthly':
            const day = config.value.dayOfMonth || 1;
            summary += `on the ${day}${getDayOrdinal(day)}`;
            break;
        case 'yearly':
            summary += 'every year';
            break;
        default:
            summary += 'as configured';
    }

    if (config.value.endDate) {
        const endDate = new Date(config.value.endDate);
        summary += ` until ${endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
    }

    return summary;
}

/**
 * Emit updated config
 */
function emitUpdate() {
    emit('update:modelValue', config.value);
}

function updateConfig(updates: Partial<RecurringConfig> | RecurringConfig) {
    config.value = {
        ...config.value,
        ...updates,
    };
    emitUpdate();
}

// ========================================================================
// WATCHERS
// ========================================================================

/**
 * Watch for external changes to modelValue
 */
watch(
    () => props.modelValue,
    (newVal) => {
        config.value = JSON.parse(JSON.stringify(newVal || {
            enabled: false,
            frequency: 'daily',
            daysOfWeek: [1, 2, 3, 4, 5],
        }));
    },
    { immediate: true, deep: true },
);
</script>
